import React, { useState, useEffect, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { Lock, LogOut, Plus, Trash2, Upload, Save, ArrowLeft, ArrowUp, ArrowDown, Type, List, Star, GripVertical, Eye } from "lucide-react";
import { invalidateBlogs } from "../lib/blogsApi";
import { invalidateCases } from "../lib/casesApi";

// Cookie-based admin auth (Step 8). Every request carries the HttpOnly
// switch_admin cookie via credentials:'include'. No token in localStorage.
const xf = (url, opts = {}) => fetch(url, { credentials: "include", ...opts });
import { invalidateHomeCopy } from "../lib/siteContent";
import { invalidateSituations } from "../lib/situationsApi";
import { invalidateAbout } from "../lib/aboutApi";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITUATIONS = ["Growth Has Stalled", "Ready to Scale", "Reposition & Transform", "Create Enterprise Value"];

const HOME_GROUPS = [
  { title: "Hero", fields: [["heroEyebrow", "Eyebrow (small label)", false], ["heroHeading", "Headline", true], ["heroSubhead", "Sub-headline", true], ["heroCta1", "Primary button label", false], ["heroCta2", "Secondary button label", false]] },
  { title: "The problem (What we solve)", fields: [["problemEyebrow", "Eyebrow", false], ["problemHeading", "Heading", true], ["problemIntro", "Intro paragraph", true]] },
  { title: "The SWITCH model (How we work)", fields: [["modelEyebrow", "Eyebrow", false], ["modelHeading", "Heading", true], ["modelIntro", "Intro paragraph", true]] },
  { title: "Selected work strip", fields: [["workEyebrow", "Eyebrow", false], ["workHeading", "Heading", true], ["workIntro", "Intro paragraph", true]] },
  { title: "Switchboard / Most read", fields: [["insightsEyebrow", "Eyebrow", false], ["insightsHeading", "Heading", true]] },
  { title: "Closing call-to-action", fields: [["closingEyebrow", "Eyebrow", false], ["closingHeading", "Heading", true], ["closingSubhead", "Sub-headline", true], ["closingCta1", "Primary button label", false], ["closingCta2", "Secondary button label", false]] },
];

const WRAP = "min-h-screen bg-[#161A15] font-body text-white";
const INPUT = "w-full border border-white/25 bg-transparent px-4 py-3 text-base text-white outline-none transition-colors placeholder:text-white/40 focus:border-[#5f8f10]";
const LBL = "font-body mb-2 block text-xs font-semibold uppercase tracking-[0.12em] text-white/50";
const BTN = "inline-flex items-center gap-2 border border-[#5f8f10] bg-[#5f8f10] px-4 py-2.5 text-sm font-semibold uppercase tracking-[0.1em] text-[#161A15] transition-colors hover:bg-[#4a700c]";
const GHOST = "inline-flex items-center gap-2 border border-white/25 px-4 py-2.5 text-sm font-semibold uppercase tracking-[0.1em] text-white/80 transition-colors hover:border-[#5f8f10] hover:text-[#5f8f10]";
const SMALL = "inline-flex items-center gap-1.5 border border-white/20 px-2.5 py-1.5 text-xs font-semibold uppercase tracking-[0.08em] text-white/70 transition-colors hover:border-[#5f8f10] hover:text-[#5f8f10]";
const ICON = "inline-flex h-9 w-9 shrink-0 items-center justify-center border border-white/20 text-white/50 transition-colors hover:border-red-400 hover:text-red-400";

// --- storage <-> editor conversion for the blog section block model ---
const toEditorSections = (sections = []) => sections.map((s) => ({
  h: s.h || "",
  b: (s.b || []).map((block) =>
    typeof block === "string"
      ? { type: "p", text: block }
      : { type: "ul", items: (block.ul || []).map((it) => (typeof it === "string" ? { lead: "", t: it } : { lead: it.lead || "", t: it.t || "" })) }),
}));

const fromEditorSections = (sections = []) => sections
  .map((s) => ({
    h: (s.h || "").trim(),
    b: (s.b || [])
      .map((block) =>
        block.type === "p"
          ? (block.text || "").trim()
          : { ul: (block.items || []).filter((it) => (it.t || "").trim()).map((it) => ((it.lead || "").trim() ? { lead: it.lead.trim(), t: it.t.trim() } : it.t.trim())) })
      .filter((block) => (typeof block === "string" ? block : block.ul.length)),
  }))
  .filter((s) => s.h || s.b.length);

const slugify = (v) => v.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

// --- reusable list of simple text/textarea rows ---
const RepeatList = ({ label, items, onChange, textarea, placeholder, testId }) => (
  <div data-testid={testId}>
    <label className={LBL}>{label}</label>
    <div className="space-y-2">
      {(items || []).map((v, i) => (
        <div key={i} className="flex items-start gap-2">
          {textarea ? (
            <textarea rows={3} className={INPUT} value={v} placeholder={placeholder} onChange={(e) => { const n = [...items]; n[i] = e.target.value; onChange(n); }} />
          ) : (
            <input className={INPUT} value={v} placeholder={placeholder} onChange={(e) => { const n = [...items]; n[i] = e.target.value; onChange(n); }} />
          )}
          <button type="button" aria-label="Remove" className={ICON} onClick={() => onChange(items.filter((_, j) => j !== i))}><Trash2 size={14} /></button>
        </div>
      ))}
    </div>
    <button type="button" className={`${SMALL} mt-2`} onClick={() => onChange([...(items || []), ""])}><Plus size={13} /> Add</button>
  </div>
);

const MetricList = ({ items, onChange }) => (
  <div data-testid="f-metrics">
    <label className={LBL}>Outcome metrics (big number + label)</label>
    <div className="space-y-2">
      {(items || []).map((m, i) => (
        <div key={i} className="flex items-start gap-2">
          <input className={`${INPUT} md:w-44`} value={m.metric} placeholder="54%" onChange={(e) => { const n = [...items]; n[i] = { ...n[i], metric: e.target.value }; onChange(n); }} />
          <input className={INPUT} value={m.label} placeholder="growth in revenue" onChange={(e) => { const n = [...items]; n[i] = { ...n[i], label: e.target.value }; onChange(n); }} />
          <button type="button" aria-label="Remove metric" className={ICON} onClick={() => onChange(items.filter((_, j) => j !== i))}><Trash2 size={14} /></button>
        </div>
      ))}
    </div>
    <button type="button" className={`${SMALL} mt-2`} onClick={() => onChange([...(items || []), { metric: "", label: "" }])}><Plus size={13} /> Add metric</button>
  </div>
);

export default function Admin() {
  const [signedIn, setSignedIn] = useState(false);
  const [authProbed, setAuthProbed] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [tab, setTab] = useState("blogs");
  const [blogs, setBlogs] = useState(null);
  const [cases, setCases] = useState(null);
  const [situations, setSituations] = useState([]);
  const [roles, setRoles] = useState([]);
  const [home, setHome] = useState(null);
  const [about, setAbout] = useState(null);
  const [previewToken, setPreviewToken] = useState("");
  const [dragSlug, setDragSlug] = useState(null);
  const [editing, setEditing] = useState(null);
  const [editKind, setEditKind] = useState("blog");
  const [isNew, setIsNew] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  // authH kept as an empty spread for backward-compat with existing call
  // sites. Cookie carries the credential now.
  const authH = {};
  // Legacy `token` variable retained as a boolean proxy so the many
  // `if (token) ...` guards throughout this component still work unchanged.
  const token = signedIn;

  const load = useCallback(async () => {
    const [rb, rc, rs, rr, rcfg] = await Promise.all([
      xf(`${API}/admin/blogs`),
      xf(`${API}/admin/cases`),
      xf(`${API}/admin/situations`),
      xf(`${API}/admin/careers`),
      xf(`${API}/admin/config`),
    ]);
    if (rb.status === 401 || rc.status === 401) { setSignedIn(false); return; }
    const jb = await rb.json();
    const jc = await rc.json();
    const js = await rs.json();
    const jr = await rr.json();
    const jcfg = await rcfg.json().catch(() => ({}));
    setBlogs((jb.blogs || []).sort((a, b) => (parseInt(a.number, 10) || 0) - (parseInt(b.number, 10) || 0)));
    setCases((jc.cases || []).sort((a, b) => (a.order ?? 0) - (b.order ?? 0)));
    setSituations((js.situations || []).sort((a, b) => (a.order ?? 0) - (b.order ?? 0)));
    setRoles((jr.roles || jr.careers || []).sort((a, b) => (a.order ?? 0) - (b.order ?? 0)));
    setPreviewToken(jcfg.previewToken || "");
  }, []);

  // Boot: probe existing cookie so a refreshed page stays signed in.
  useEffect(() => {
    let cancelled = false;
    xf(`${API}/auth/me`)
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (!cancelled) { setSignedIn(!!(j && j.email)); setAuthProbed(true); } })
      .catch(() => { if (!cancelled) setAuthProbed(true); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => { if (signedIn) load(); }, [signedIn, load]);

  useEffect(() => {
    if (signedIn && tab === "home" && home === null) {
      xf(`${API}/admin/site/homepage`).then((r) => r.json()).then(setHome).catch(() => {});
    }
    if (signedIn && tab === "about" && about === null) {
      xf(`${API}/admin/site/about`).then((r) => r.json()).then(setAbout).catch(() => {});
    }
  }, [signedIn, tab, home, about]);

  const login = async (e) => {
    e.preventDefault(); setErr("");
    const res = await xf(`${API}/auth/login`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email, password }) });
    if (!res.ok) { setErr("Invalid email or password."); return; }
    setPassword("");
    setSignedIn(true);
  };
  const logout = async () => {
    try { await xf(`${API}/auth/logout`, { method: "POST" }); } catch { /* ignore */ }
    setSignedIn(false); setBlogs(null); setCases(null);
  };

  const nextNumber = () => {
    const nums = (blogs || []).map((b) => parseInt(b.number, 10)).filter((n) => !isNaN(n));
    return String((nums.length ? Math.max(...nums) : 0) + 1).padStart(2, "0");
  };

  const startNewBlog = () => {
    setEditKind("blog"); setIsNew(true); setErr(""); setMsg("");
    setEditing({ slug: "", number: nextNumber(), title: "", excerpt: "", tags: [], situation: "Growth Has Stalled", readTime: "6 min read", image: "", intro: [""], sections: [], tldr: [], sources: [], signoff: "", seoTitle: "", seoImage: "", publishAt: "", featured: false, published: false });
  };
  const editBlog = (b) => {
    setEditKind("blog"); setIsNew(false); setErr(""); setMsg("");
    setEditing({ ...b, intro: b.intro && b.intro.length ? b.intro : [""], tldr: b.tldr || [], sources: b.sources || [], signoff: b.signoff || "", seoTitle: b.seoTitle || "", seoImage: b.seoImage || "", publishAt: b.publishAt || "", featured: !!b.featured, sections: toEditorSections(b.sections) });
  };
  const startNewCase = () => {
    setEditKind("case"); setIsNew(true); setErr(""); setMsg("");
    setEditing({ slug: "", kicker: "", client: "", service: "", location: "", situation: "Growth Has Stalled", image: "", headline: "", opportunity: [""], role: [""], metrics: [{ metric: "", label: "" }], outcomes: [], seoTitle: "", seoImage: "", published: false });
  };
  const editCase = (c) => {
    setEditKind("case"); setIsNew(false); setErr(""); setMsg("");
    setEditing({ ...c, opportunity: c.opportunity && c.opportunity.length ? c.opportunity : [""], role: c.role && c.role.length ? c.role : [""], metrics: c.metrics && c.metrics.length ? c.metrics : [{ metric: "", label: "" }], outcomes: c.outcomes || [], seoTitle: c.seoTitle || "", seoImage: c.seoImage || "" });
  };
  const editSituation = (s) => {
    setEditKind("situation"); setIsNew(false); setErr(""); setMsg("");
    setEditing({ ...s, intro: s.intro && s.intro.length ? s.intro : [""], sections: (s.sections || []).map((x) => ({ kicker: x.kicker || "", heading: x.heading || "", body: x.body && x.body.length ? x.body : [""], items: x.items || [] })) });
  };
  const startNewRole = () => {
    setEditKind("role"); setIsNew(true); setErr(""); setMsg("");
    setEditing({ slug: "", title: "", location: "", type: "Full-time", summary: "", description: "", active: true });
  };
  const editRole = (r) => {
    setEditKind("role"); setIsNew(false); setErr(""); setMsg("");
    setEditing({ ...r, active: r.active !== false });
  };

  const setF = (k, v) => setEditing((e) => ({ ...e, [k]: v }));

  // blog section / block / item mutators
  const setSection = (si, patch) => setF("sections", editing.sections.map((s, i) => (i === si ? { ...s, ...patch } : s)));
  const addSection = () => setF("sections", [...(editing.sections || []), { h: "", b: [] }]);
  const removeSection = (si) => setF("sections", editing.sections.filter((_, i) => i !== si));
  const moveSection = (si, dir) => { const arr = [...editing.sections]; const j = si + dir; if (j < 0 || j >= arr.length) return; [arr[si], arr[j]] = [arr[j], arr[si]]; setF("sections", arr); };
  const setBlock = (si, bi, patch) => setSection(si, { b: editing.sections[si].b.map((b, i) => (i === bi ? { ...b, ...patch } : b)) });
  const addBlock = (si, type) => setSection(si, { b: [...editing.sections[si].b, type === "p" ? { type: "p", text: "" } : { type: "ul", items: [{ lead: "", t: "" }] }] });
  const removeBlock = (si, bi) => setSection(si, { b: editing.sections[si].b.filter((_, i) => i !== bi) });
  const setItem = (si, bi, ii, patch) => setBlock(si, bi, { items: editing.sections[si].b[bi].items.map((it, i) => (i === ii ? { ...it, ...patch } : it)) });
  const addItem = (si, bi) => setBlock(si, bi, { items: [...editing.sections[si].b[bi].items, { lead: "", t: "" }] });
  const removeItem = (si, bi, ii) => setBlock(si, bi, { items: editing.sections[si].b[bi].items.filter((_, i) => i !== ii) });

  // situation section mutators
  const setSit = (si, patch) => setF("sections", editing.sections.map((s, i) => (i === si ? { ...s, ...patch } : s)));
  const addSit = () => setF("sections", [...(editing.sections || []), { kicker: "", heading: "", body: [""], items: [] }]);
  const removeSit = (si) => setF("sections", editing.sections.filter((_, i) => i !== si));
  const moveSit = (si, dir) => { const a = [...editing.sections]; const j = si + dir; if (j < 0 || j >= a.length) return; [a[si], a[j]] = [a[j], a[si]]; setF("sections", a); };

  const cleanStrings = (arr) => (arr || []).map((s) => s.trim()).filter(Boolean);

  const save = async () => {
    setErr(""); setMsg(""); setSaving(true);
    let body, url, method = isNew ? "POST" : "PUT";
    if (editKind === "blog") {
      body = {
        ...editing,
        intro: cleanStrings(editing.intro),
        tldr: cleanStrings(editing.tldr),
        sources: cleanStrings(editing.sources),
        signoff: (editing.signoff || "").trim(),
        featured: !!editing.featured,
        sections: fromEditorSections(editing.sections),
      };
      url = isNew ? `${API}/admin/blogs` : `${API}/admin/blogs/${editing.slug}`;
    } else if (editKind === "case") {
      body = {
        ...editing,
        opportunity: cleanStrings(editing.opportunity),
        role: cleanStrings(editing.role),
        outcomes: cleanStrings(editing.outcomes),
        metrics: (editing.metrics || []).filter((m) => (m.metric || "").trim()).map((m) => ({ metric: m.metric.trim(), label: (m.label || "").trim() })),
      };
      url = isNew ? `${API}/admin/cases` : `${API}/admin/cases/${editing.slug}`;
    } else if (editKind === "role") {
      body = { ...editing, active: !!editing.active };
      url = isNew ? `${API}/admin/careers` : `${API}/admin/careers/${editing.slug}`;
    } else {
      body = {
        title: (editing.title || "").trim(),
        intro: cleanStrings(editing.intro),
        sections: (editing.sections || [])
          .map((s) => ({ kicker: (s.kicker || "").trim(), heading: (s.heading || "").trim(), body: cleanStrings(s.body), items: cleanStrings(s.items) }))
          .filter((s) => s.heading || s.body.length),
      };
      url = `${API}/admin/situations/${editing.slug}`; method = "PUT";
    }
    if (editKind !== "situation" && !body.slug) { setErr("A slug is required."); setSaving(false); return; }
    if (editKind === "blog" && !body.title) { setErr("A title is required."); setSaving(false); return; }
    if (editKind === "case" && !body.headline) { setErr("A headline is required."); setSaving(false); return; }
    if (editKind === "role" && !body.title) { setErr("A title is required."); setSaving(false); return; }
    const res = await xf(url, { method, headers: { ...authH, "Content-Type": "application/json" }, body: JSON.stringify(body) });
    setSaving(false);
    if (!res.ok) { const j = await res.json().catch(() => ({})); setErr(j.detail || "Could not save."); return; }
    setMsg(isNew ? "Created." : "Changes saved."); setEditing(null);
    if (editKind === "blog") invalidateBlogs(); else if (editKind === "case") invalidateCases(); else if (editKind === "situation") invalidateSituations();
    load();
  };

  const saveAbout = async () => {
    setErr(""); setMsg(""); setSaving(true);
    const res = await xf(`${API}/admin/site/about`, { method: "PUT", headers: { ...authH, "Content-Type": "application/json" }, body: JSON.stringify(about) });
    setSaving(false);
    if (!res.ok) { setErr("Could not save About content."); return; }
    setMsg("About content saved."); invalidateAbout();
  };
  const remove = async (kind, slug) => {
    if (!window.confirm(`Delete "${slug}"? This cannot be undone.`)) return;
    await xf(`${API}/admin/${kind === "blog" ? "blogs" : "cases"}/${slug}`, { method: "DELETE", headers: authH });
    if (kind === "blog") invalidateBlogs(); else invalidateCases();
    load();
  };
  const upload = async (file, field = "image") => {
    if (!file) return;
    setErr(""); setMsg("Uploading image…");
    const fd = new FormData(); fd.append("file", file);
    const res = await xf(`${API}/admin/upload`, { method: "POST", headers: authH, body: fd });
    if (res.status === 503) { setMsg(""); setErr("Image storage is not connected yet. Paste an image URL below for now."); return; }
    if (!res.ok) { setMsg(""); setErr("Upload failed. Try again or paste an image URL."); return; }
    const j = await res.json(); setF(field, j.url); setMsg("Image uploaded."); setErr("");
  };

  const copyPreview = (kind, slug) => {
    if (!previewToken) { setErr("Preview token unavailable."); return; }
    const path = kind === "blog" ? `/insights/${slug}` : `/our-work/${slug}`;
    const url = `${window.location.origin}${path}?preview=${encodeURIComponent(previewToken)}`;
    navigator.clipboard.writeText(url).then(() => setMsg("Preview link copied to clipboard.")).catch(() => setErr("Could not copy link."));
  };

  const persistCaseOrder = async (list) => {
    setCases(list);
    await xf(`${API}/admin/reorder-cases`, { method: "POST", headers: { ...authH, "Content-Type": "application/json" }, body: JSON.stringify({ order: list.map((c) => c.slug) }) });
    invalidateCases();
  };
  const onDropCase = (targetSlug) => {
    if (!dragSlug || dragSlug === targetSlug) { setDragSlug(null); return; }
    const arr = [...cases];
    const from = arr.findIndex((c) => c.slug === dragSlug);
    const to = arr.findIndex((c) => c.slug === targetSlug);
    if (from < 0 || to < 0) { setDragSlug(null); return; }
    const [m] = arr.splice(from, 1); arr.splice(to, 0, m);
    persistCaseOrder(arr); setDragSlug(null);
  };
  const moveCase = (slug, dir) => {
    const arr = [...cases];
    const i = arr.findIndex((c) => c.slug === slug);
    const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    persistCaseOrder(arr);
  };

  const saveHome = async () => {
    setErr(""); setMsg(""); setSaving(true);
    const res = await xf(`${API}/admin/site/homepage`, { method: "PUT", headers: { ...authH, "Content-Type": "application/json" }, body: JSON.stringify(home) });
    setSaving(false);
    if (!res.ok) { setErr("Could not save homepage copy."); return; }
    setMsg("Homepage copy saved."); invalidateHomeCopy();
  };

  // ---------------- LOGIN ----------------
  if (!token || blogs === null || cases === null) {
    return (
      <div className={`${WRAP} flex items-center justify-center px-6`}>
        <Helmet><title>Admin | SWITCH</title><meta name="robots" content="noindex, nofollow" /></Helmet>
        <form onSubmit={login} data-testid="admin-login" className="w-full max-w-sm space-y-4">
          <div className="flex items-center gap-2 text-[#5f8f10]"><Lock size={16} /><span className="font-display text-2xl uppercase tracking-tight">SWITCH Admin</span></div>
          <p className="font-body text-sm text-white/50">Sign in to manage the website.</p>
          <input type="email" required data-testid="admin-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className={INPUT} />
          <input type="password" required data-testid="admin-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" className={INPUT} />
          <button type="submit" data-testid="admin-signin" className={`${BTN} w-full justify-center`}>Sign in</button>
          {err && <p className="text-sm font-semibold text-red-400">{err}</p>}
        </form>
      </div>
    );
  }

  // ---------------- BLOG EDITOR ----------------
  if (editing && editKind === "blog") {
    return (
      <div className={`${WRAP} px-6 py-10 md:px-12`}>
        <Helmet><title>Edit article | SWITCH Admin</title><meta name="robots" content="noindex, nofollow" /></Helmet>
        <div className="mx-auto max-w-3xl">
          <button onClick={() => setEditing(null)} className={`${GHOST} mb-6`}><ArrowLeft size={15} /> Back to list</button>
          <h1 className="font-display text-3xl uppercase tracking-tight">{isNew ? "New article" : `Edit: ${editing.slug}`}</h1>

          <div className="mt-8 space-y-6">
            <div className="grid gap-5 md:grid-cols-2">
              <div><label className={LBL}>Slug (URL, e.g. my-article)</label><input className={INPUT} data-testid="f-slug" value={editing.slug} disabled={!isNew} onChange={(e) => setF("slug", slugify(e.target.value))} /></div>
              <div><label className={LBL}>Number (e.g. 09)</label><input className={INPUT} data-testid="f-number" value={editing.number} onChange={(e) => setF("number", e.target.value)} /></div>
            </div>
            <div><label className={LBL}>Title</label><input className={INPUT} data-testid="f-title" value={editing.title} onChange={(e) => setF("title", e.target.value)} /></div>
            <div><label className={LBL}>Excerpt (one-line summary shown on cards)</label><textarea className={INPUT} rows={2} data-testid="f-excerpt" value={editing.excerpt} onChange={(e) => setF("excerpt", e.target.value)} /></div>
            <div className="grid gap-5 md:grid-cols-3">
              <div><label className={LBL}>Tags (comma separated)</label><input className={INPUT} data-testid="f-tags" value={(editing.tags || []).join(", ")} onChange={(e) => setF("tags", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} /></div>
              <div><label className={LBL}>Growth situation</label><select className={INPUT} data-testid="f-situation" value={editing.situation} onChange={(e) => setF("situation", e.target.value)}>{SITUATIONS.map((s) => <option key={s} className="bg-[#161A15]">{s}</option>)}</select></div>
              <div><label className={LBL}>Read time</label><input className={INPUT} data-testid="f-readtime" value={editing.readTime} onChange={(e) => setF("readTime", e.target.value)} /></div>
            </div>

            <div>
              <label className={LBL}>Cover image</label>
              <div className="flex flex-wrap items-center gap-3">
                <input className={`${INPUT} flex-1`} data-testid="f-image" value={editing.image} onChange={(e) => setF("image", e.target.value)} placeholder="/assets/... or https://..." />
                <label className={`${GHOST} cursor-pointer`}><Upload size={15} /> Upload<input type="file" accept="image/*" className="hidden" data-testid="f-image-file" onChange={(e) => upload(e.target.files[0])} /></label>
              </div>
              {editing.image && <img src={editing.image} alt="" className="mt-3 aspect-[16/9] w-64 object-cover" />}
            </div>

            <div className="border-t border-white/10 pt-6"><RepeatList testId="f-intro" label="Intro paragraphs" items={editing.intro} onChange={(v) => setF("intro", v)} textarea placeholder="Opening paragraph…" /></div>
            <div className="border-t border-white/10 pt-6"><RepeatList testId="f-tldr" label="TL;DR bullets (short takeaways)" items={editing.tldr} onChange={(v) => setF("tldr", v)} placeholder="A one-line takeaway…" /></div>

            <div className="border-t border-white/10 pt-6">
              <div className="flex items-center justify-between">
                <label className={`${LBL} mb-0`}>Body sections</label>
                <button type="button" data-testid="section-add" className={SMALL} onClick={addSection}><Plus size={13} /> Add section</button>
              </div>
              <div className="mt-4 space-y-6" data-testid="sections-editor">
                {(editing.sections || []).map((sec, si) => (
                  <div key={si} className="border border-white/15 p-4 md:p-5" data-testid={`section-${si}`}>
                    <div className="flex items-start gap-2">
                      <input className={INPUT} data-testid={`section-heading-${si}`} value={sec.h} placeholder="Section heading" onChange={(e) => setSection(si, { h: e.target.value })} />
                      <button type="button" aria-label="Move up" className={ICON} onClick={() => moveSection(si, -1)}><ArrowUp size={14} /></button>
                      <button type="button" aria-label="Move down" className={ICON} onClick={() => moveSection(si, 1)}><ArrowDown size={14} /></button>
                      <button type="button" aria-label="Delete section" className={ICON} onClick={() => removeSection(si)}><Trash2 size={14} /></button>
                    </div>
                    <div className="mt-4 space-y-4">
                      {sec.b.map((block, bi) => (
                        <div key={bi} className="border border-white/10 bg-white/[0.03] p-3">
                          <div className="mb-2 flex items-center justify-between">
                            <span className="font-body text-[10px] font-semibold uppercase tracking-[0.14em] text-[#5f8f10]">{block.type === "p" ? "Paragraph" : "Bullet list"}</span>
                            <button type="button" aria-label="Remove block" className="text-white/40 transition-colors hover:text-red-400" onClick={() => removeBlock(si, bi)}><Trash2 size={13} /></button>
                          </div>
                          {block.type === "p" ? (
                            <textarea rows={4} className={INPUT} data-testid={`block-p-${si}-${bi}`} value={block.text} placeholder="Paragraph text…" onChange={(e) => setBlock(si, bi, { text: e.target.value })} />
                          ) : (
                            <div className="space-y-2">
                              {block.items.map((it, ii) => (
                                <div key={ii} className="flex items-start gap-2">
                                  <input className={`${INPUT} md:w-56`} value={it.lead} placeholder="Bold intro (optional)" onChange={(e) => setItem(si, bi, ii, { lead: e.target.value })} />
                                  <input className={INPUT} value={it.t} placeholder="Bullet text" onChange={(e) => setItem(si, bi, ii, { t: e.target.value })} />
                                  <button type="button" aria-label="Remove bullet" className={ICON} onClick={() => removeItem(si, bi, ii)}><Trash2 size={13} /></button>
                                </div>
                              ))}
                              <button type="button" className={SMALL} onClick={() => addItem(si, bi)}><Plus size={12} /> Add bullet</button>
                            </div>
                          )}
                        </div>
                      ))}
                      <div className="flex flex-wrap gap-2">
                        <button type="button" data-testid={`add-paragraph-${si}`} className={SMALL} onClick={() => addBlock(si, "p")}><Type size={12} /> Add paragraph</button>
                        <button type="button" data-testid={`add-list-${si}`} className={SMALL} onClick={() => addBlock(si, "ul")}><List size={12} /> Add bullet list</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="border-t border-white/10 pt-6"><label className={LBL}>Sign-off line (optional closing statement)</label><input className={INPUT} data-testid="f-signoff" value={editing.signoff} onChange={(e) => setF("signoff", e.target.value)} placeholder="A memorable closing line…" /></div>
            <div className="border-t border-white/10 pt-6"><RepeatList testId="f-sources" label="Sources (optional)" items={editing.sources} onChange={(v) => setF("sources", v)} placeholder="Source name / citation…" /></div>

            <div className="border-t border-white/10 pt-6" data-testid="blog-seo-fields">
              <label className={`${LBL} text-[#5f8f10]`}>SEO &amp; social sharing (optional)</label>
              <div className="space-y-4">
                <div><label className={LBL}>Custom meta / share title</label><input className={INPUT} data-testid="f-seotitle" value={editing.seoTitle} onChange={(e) => setF("seoTitle", e.target.value)} placeholder="Overrides the title on Google & social cards" /></div>
                <div>
                  <label className={LBL}>Social share image (falls back to cover)</label>
                  <div className="flex flex-wrap items-center gap-3">
                    <input className={`${INPUT} flex-1`} data-testid="f-seoimage" value={editing.seoImage} onChange={(e) => setF("seoImage", e.target.value)} placeholder="/assets/... or https://..." />
                    <label className={`${GHOST} cursor-pointer`}><Upload size={15} /> Upload<input type="file" accept="image/*" className="hidden" data-testid="f-seoimage-file" onChange={(e) => upload(e.target.files[0], "seoImage")} /></label>
                  </div>
                </div>
              </div>
            </div>

            <div className="border-t border-white/10 pt-6 space-y-3">
              <div>
                <label className={LBL}>Schedule publish date (optional: go live automatically)</label>
                <input type="datetime-local" className={`${INPUT} md:w-72`} data-testid="f-publishat" value={(editing.publishAt || "").slice(0, 16)} onChange={(e) => setF("publishAt", e.target.value)} />
                <p className="font-body mt-1 text-xs text-white/40">Leave empty to publish immediately once "Published" is ticked. A future date keeps it hidden until then.</p>
              </div>
              <label className="flex items-center gap-3"><input type="checkbox" data-testid="f-featured" checked={!!editing.featured} onChange={(e) => setF("featured", e.target.checked)} /> <span className="font-body inline-flex items-center gap-2 text-sm uppercase tracking-[0.1em]"><Star size={14} className="text-[#5f8f10]" /> Featured (pin to top of Switchboard + homepage)</span></label>
              <label className="flex items-center gap-3"><input type="checkbox" data-testid="f-published" checked={!!editing.published} onChange={(e) => setF("published", e.target.checked)} /> <span className="font-body text-sm uppercase tracking-[0.1em]">Published (live on the site)</span></label>
              <div className="mt-3 flex items-center gap-3">
                <button onClick={save} disabled={saving} data-testid="admin-save" className={`${BTN} disabled:opacity-60`}><Save size={15} /> {saving ? "Saving…" : "Save article"}</button>
                <button onClick={() => setEditing(null)} className={GHOST}>Cancel</button>
                {err && <span className="text-sm font-semibold text-red-400">{err}</span>}
                {msg && <span className="text-sm font-semibold text-[#5f8f10]">{msg}</span>}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ---------------- CASE EDITOR ----------------
  if (editing && editKind === "case") {
    return (
      <div className={`${WRAP} px-6 py-10 md:px-12`}>
        <Helmet><title>Edit case study | SWITCH Admin</title><meta name="robots" content="noindex, nofollow" /></Helmet>
        <div className="mx-auto max-w-3xl">
          <button onClick={() => setEditing(null)} className={`${GHOST} mb-6`}><ArrowLeft size={15} /> Back to list</button>
          <h1 className="font-display text-3xl uppercase tracking-tight">{isNew ? "New case study" : `Edit: ${editing.slug}`}</h1>

          <div className="mt-8 space-y-6">
            <div className="grid gap-5 md:grid-cols-2">
              <div><label className={LBL}>Slug (URL, e.g. data-into-growth)</label><input className={INPUT} data-testid="cf-slug" value={editing.slug} disabled={!isNew} onChange={(e) => setF("slug", slugify(e.target.value))} /></div>
              <div><label className={LBL}>Kicker (short label, e.g. Data into Growth)</label><input className={INPUT} data-testid="cf-kicker" value={editing.kicker} onChange={(e) => setF("kicker", e.target.value)} /></div>
            </div>
            <div><label className={LBL}>Headline (the outcome statement)</label><textarea className={INPUT} rows={2} data-testid="cf-headline" value={editing.headline} onChange={(e) => setF("headline", e.target.value)} placeholder="Turning … into …" /></div>
            <div className="grid gap-5 md:grid-cols-2">
              <div><label className={LBL}>Client (descriptor)</label><input className={INPUT} data-testid="cf-client" value={editing.client} onChange={(e) => setF("client", e.target.value)} placeholder="Premium Food Retail Chain" /></div>
              <div><label className={LBL}>Engagement / service</label><input className={INPUT} data-testid="cf-service" value={editing.service} onChange={(e) => setF("service", e.target.value)} placeholder="Customer Revenue Expansion" /></div>
              <div><label className={LBL}>Market / location</label><input className={INPUT} data-testid="cf-location" value={editing.location} onChange={(e) => setF("location", e.target.value)} placeholder="Mumbai, India" /></div>
              <div><label className={LBL}>Growth situation</label><select className={INPUT} data-testid="cf-situation" value={editing.situation} onChange={(e) => setF("situation", e.target.value)}>{SITUATIONS.map((s) => <option key={s} className="bg-[#161A15]">{s}</option>)}</select></div>
            </div>

            <div>
              <label className={LBL}>Cover image</label>
              <div className="flex flex-wrap items-center gap-3">
                <input className={`${INPUT} flex-1`} data-testid="cf-image" value={editing.image} onChange={(e) => setF("image", e.target.value)} placeholder="/assets/... or https://..." />
                <label className={`${GHOST} cursor-pointer`}><Upload size={15} /> Upload<input type="file" accept="image/*" className="hidden" data-testid="cf-image-file" onChange={(e) => upload(e.target.files[0])} /></label>
              </div>
              {editing.image && <img src={editing.image} alt="" className="mt-3 aspect-[16/9] w-64 object-cover" />}
            </div>

            <div className="border-t border-white/10 pt-6"><RepeatList testId="cf-opportunity" label="The opportunity (paragraphs)" items={editing.opportunity} onChange={(v) => setF("opportunity", v)} textarea placeholder="What was the opportunity…" /></div>
            <div className="border-t border-white/10 pt-6"><RepeatList testId="cf-role" label="Our role (bullet points)" items={editing.role} onChange={(v) => setF("role", v)} placeholder="What SWITCH did…" /></div>
            <div className="border-t border-white/10 pt-6"><MetricList items={editing.metrics} onChange={(v) => setF("metrics", v)} /></div>
            <div className="border-t border-white/10 pt-6"><RepeatList testId="cf-outcomes" label="Outcomes (optional bullet points)" items={editing.outcomes} onChange={(v) => setF("outcomes", v)} placeholder="A qualitative outcome…" /></div>

            <div className="border-t border-white/10 pt-6" data-testid="case-seo-fields">
              <label className={`${LBL} text-[#5f8f10]`}>SEO &amp; social sharing (optional)</label>
              <div className="space-y-4">
                <div><label className={LBL}>Custom meta / share title</label><input className={INPUT} data-testid="cf-seotitle" value={editing.seoTitle} onChange={(e) => setF("seoTitle", e.target.value)} placeholder="Overrides the title on Google & social cards" /></div>
                <div>
                  <label className={LBL}>Social share image (falls back to cover)</label>
                  <div className="flex flex-wrap items-center gap-3">
                    <input className={`${INPUT} flex-1`} data-testid="cf-seoimage" value={editing.seoImage} onChange={(e) => setF("seoImage", e.target.value)} placeholder="/assets/... or https://..." />
                    <label className={`${GHOST} cursor-pointer`}><Upload size={15} /> Upload<input type="file" accept="image/*" className="hidden" data-testid="cf-seoimage-file" onChange={(e) => upload(e.target.files[0], "seoImage")} /></label>
                  </div>
                </div>
              </div>
            </div>

            <div className="border-t border-white/10 pt-6 space-y-3">
              <label className="flex items-center gap-3"><input type="checkbox" data-testid="cf-published" checked={!!editing.published} onChange={(e) => setF("published", e.target.checked)} /> <span className="font-body text-sm uppercase tracking-[0.1em]">Published (live on the site)</span></label>
              <div className="mt-3 flex items-center gap-3">
                <button onClick={save} disabled={saving} data-testid="admin-save" className={`${BTN} disabled:opacity-60`}><Save size={15} /> {saving ? "Saving…" : "Save case study"}</button>
                <button onClick={() => setEditing(null)} className={GHOST}>Cancel</button>
                {err && <span className="text-sm font-semibold text-red-400">{err}</span>}
                {msg && <span className="text-sm font-semibold text-[#5f8f10]">{msg}</span>}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ---------------- SITUATION (GROWTH PAGE) EDITOR ----------------
  if (editing && editKind === "situation") {
    return (
      <div className={`${WRAP} px-6 py-10 md:px-12`}>
        <Helmet><title>Edit growth page | SWITCH Admin</title><meta name="robots" content="noindex, nofollow" /></Helmet>
        <div className="mx-auto max-w-3xl">
          <button onClick={() => setEditing(null)} className={`${GHOST} mb-6`}><ArrowLeft size={15} /> Back to list</button>
          <h1 className="font-display text-3xl uppercase tracking-tight">Edit: {editing.title}</h1>
          <p className="font-body mt-2 text-sm text-white/50">/what-we-solve/{editing.slug}</p>

          <div className="mt-8 space-y-6">
            <div><label className={LBL}>Page title</label><input className={INPUT} data-testid="gf-title" value={editing.title} onChange={(e) => setF("title", e.target.value)} /></div>
            <div className="border-t border-white/10 pt-6"><RepeatList testId="gf-intro" label="Intro paragraphs (hero)" items={editing.intro} onChange={(v) => setF("intro", v)} textarea placeholder="Opening paragraph…" /></div>

            <div className="border-t border-white/10 pt-6">
              <div className="flex items-center justify-between">
                <label className={`${LBL} mb-0`}>Body sections</label>
                <button type="button" data-testid="gf-section-add" className={SMALL} onClick={addSit}><Plus size={13} /> Add section</button>
              </div>
              <div className="mt-4 space-y-6" data-testid="gf-sections">
                {(editing.sections || []).map((sec, si) => (
                  <div key={si} className="border border-white/15 p-4 md:p-5" data-testid={`gf-section-${si}`}>
                    <div className="flex items-center justify-end gap-2">
                      <button type="button" aria-label="Move up" className={ICON} onClick={() => moveSit(si, -1)}><ArrowUp size={14} /></button>
                      <button type="button" aria-label="Move down" className={ICON} onClick={() => moveSit(si, 1)}><ArrowDown size={14} /></button>
                      <button type="button" aria-label="Delete section" className={ICON} onClick={() => removeSit(si)}><Trash2 size={14} /></button>
                    </div>
                    <div className="mt-3 grid gap-4 md:grid-cols-2">
                      <div><label className={LBL}>Kicker (small label)</label><input className={INPUT} data-testid={`gf-kicker-${si}`} value={sec.kicker} onChange={(e) => setSit(si, { kicker: e.target.value })} /></div>
                      <div><label className={LBL}>Heading</label><input className={INPUT} data-testid={`gf-heading-${si}`} value={sec.heading} onChange={(e) => setSit(si, { heading: e.target.value })} /></div>
                    </div>
                    <div className="mt-4"><RepeatList label="Body paragraphs" items={sec.body} onChange={(v) => setSit(si, { body: v })} textarea placeholder="Paragraph…" /></div>
                    <div className="mt-4"><RepeatList label="Bullet items (optional)" items={sec.items} onChange={(v) => setSit(si, { items: v })} placeholder="A bullet point…" /></div>
                  </div>
                ))}
              </div>
            </div>

            <div className="border-t border-white/10 pt-6 flex items-center gap-3">
              <button onClick={save} disabled={saving} data-testid="admin-save" className={`${BTN} disabled:opacity-60`}><Save size={15} /> {saving ? "Saving…" : "Save growth page"}</button>
              <button onClick={() => setEditing(null)} className={GHOST}>Cancel</button>
              {err && <span className="text-sm font-semibold text-red-400">{err}</span>}
              {msg && <span className="text-sm font-semibold text-[#5f8f10]">{msg}</span>}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ---------------- ROLE (CAREERS) EDITOR ----------------
  if (editing && editKind === "role") {
    return (
      <div className={`${WRAP} px-6 py-10 md:px-12`}>
        <Helmet><title>Edit role | SWITCH Admin</title><meta name="robots" content="noindex, nofollow" /></Helmet>
        <div className="mx-auto max-w-3xl">
          <button onClick={() => setEditing(null)} className={`${GHOST} mb-6`}><ArrowLeft size={15} /> Back to list</button>
          <h1 className="font-display text-3xl uppercase tracking-tight">{isNew ? "New role" : `Edit: ${editing.title}`}</h1>
          <div className="mt-8 space-y-6">
            <div className="grid gap-5 md:grid-cols-2">
              <div><label className={LBL}>Slug (URL id)</label><input className={INPUT} data-testid="rf-slug" value={editing.slug} disabled={!isNew} onChange={(e) => setF("slug", slugify(e.target.value))} /></div>
              <div><label className={LBL}>Job title</label><input className={INPUT} data-testid="rf-title" value={editing.title} onChange={(e) => setF("title", e.target.value)} /></div>
              <div><label className={LBL}>Location</label><input className={INPUT} data-testid="rf-location" value={editing.location} onChange={(e) => setF("location", e.target.value)} placeholder="Mumbai, India" /></div>
              <div><label className={LBL}>Type</label><input className={INPUT} data-testid="rf-type" value={editing.type} onChange={(e) => setF("type", e.target.value)} placeholder="Full-time" /></div>
            </div>
            <div><label className={LBL}>Summary (one line)</label><textarea rows={2} className={INPUT} data-testid="rf-summary" value={editing.summary} onChange={(e) => setF("summary", e.target.value)} /></div>
            <div><label className={LBL}>Description</label><textarea rows={5} className={INPUT} data-testid="rf-description" value={editing.description} onChange={(e) => setF("description", e.target.value)} /></div>
            <label className="flex items-center gap-3"><input type="checkbox" data-testid="rf-active" checked={!!editing.active} onChange={(e) => setF("active", e.target.checked)} /> <span className="font-body text-sm uppercase tracking-[0.1em]">Active (visible on Careers page)</span></label>
            <div className="border-t border-white/10 pt-6 flex items-center gap-3">
              <button onClick={save} disabled={saving} data-testid="admin-save" className={`${BTN} disabled:opacity-60`}><Save size={15} /> {saving ? "Saving…" : "Save role"}</button>
              <button onClick={() => setEditing(null)} className={GHOST}>Cancel</button>
              {err && <span className="text-sm font-semibold text-red-400">{err}</span>}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ---------------- LIST ----------------
  const TabBtn = ({ id, label }) => (
    <button type="button" data-testid={`admin-tab-${id}`} onClick={() => setTab(id)} className={`font-body border px-4 py-2 text-sm font-semibold uppercase tracking-[0.1em] transition-colors ${tab === id ? "border-[#5f8f10] bg-[#5f8f10] text-[#161A15]" : "border-white/25 text-white/70 hover:border-[#5f8f10] hover:text-[#5f8f10]"}`}>{label}</button>
  );

  return (
    <div className={`${WRAP} px-6 py-10 md:px-12`}>
      <Helmet><title>Admin | SWITCH</title><meta name="robots" content="noindex, nofollow" /></Helmet>
      <div className="mx-auto max-w-4xl">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <h1 className="font-display text-3xl uppercase tracking-tight">Content manager</h1>
          <button onClick={logout} data-testid="admin-logout" className={GHOST}><LogOut size={15} /> Sign out</button>
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-3">
          <TabBtn id="blogs" label={`Articles (${blogs.length})`} />
          <TabBtn id="cases" label={`Case studies (${cases.length})`} />
          <TabBtn id="growth" label="Growth pages" />
          <TabBtn id="careers" label={`Careers (${roles.length})`} />
          <TabBtn id="about" label="About" />
          <TabBtn id="home" label="Homepage" />
          <div className="ml-auto">
            {tab === "blogs" && <button onClick={startNewBlog} data-testid="admin-new" className={BTN}><Plus size={15} /> New article</button>}
            {tab === "cases" && <button onClick={startNewCase} data-testid="admin-new-case" className={BTN}><Plus size={15} /> New case study</button>}
            {tab === "careers" && <button onClick={startNewRole} data-testid="admin-new-role" className={BTN}><Plus size={15} /> New role</button>}
          </div>
        </div>
        {msg && <p className="mt-4 text-sm font-semibold text-[#5f8f10]">{msg}</p>}

        {tab === "careers" && (
          <div className="mt-6 border-t border-white/12" data-testid="admin-careers-list">
            {roles.length === 0 && <p className="font-body py-4 text-sm text-white/40">No roles yet. Click "New role" to post an opening.</p>}
            {roles.map((r) => (
              <div key={r.slug} className="flex items-center justify-between gap-4 border-b border-white/12 py-4">
                <div>
                  <p className="font-body text-base">{r.title}</p>
                  <p className="font-body text-xs uppercase tracking-[0.1em] text-white/40">{[r.location, r.type].filter(Boolean).join(" · ")} {r.active ? "" : "· HIDDEN"}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => editRole(r)} data-testid={`admin-edit-role-${r.slug}`} className={GHOST}>Edit</button>
                  <button onClick={() => remove("role", r.slug)} data-testid={`admin-delete-role-${r.slug}`} aria-label="Delete" className={ICON}><Trash2 size={15} /></button>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "about" && (
          <div className="mt-6" data-testid="admin-about-editor">
            {about === null ? <p className="font-body text-white/50">Loading…</p> : (
              <div className="space-y-8">
                <div className="border border-white/12 p-4 md:p-5">
                  <h3 className="font-display mb-4 text-lg uppercase tracking-tight text-[#5f8f10]">Our story</h3>
                  <div className="space-y-4">
                    <div><label className={LBL}>Eyebrow</label><input className={INPUT} data-testid="af-storyEyebrow" value={about.storyEyebrow || ""} onChange={(e) => setAbout({ ...about, storyEyebrow: e.target.value })} /></div>
                    <div><label className={LBL}>Heading</label><input className={INPUT} data-testid="af-storyHeading" value={about.storyHeading || ""} onChange={(e) => setAbout({ ...about, storyHeading: e.target.value })} /></div>
                    <RepeatList testId="af-story" label="Story paragraphs" items={about.story || []} onChange={(v) => setAbout({ ...about, story: v })} textarea placeholder="A paragraph of the story…" />
                  </div>
                </div>
                <div className="border border-white/12 p-4 md:p-5">
                  <div className="flex items-center justify-between">
                    <h3 className="font-display text-lg uppercase tracking-tight text-[#5f8f10]">Team &amp; leadership</h3>
                    <button type="button" data-testid="af-add-leader" className={SMALL} onClick={() => setAbout({ ...about, leaders: [...(about.leaders || []), { name: "", role: "", bio: "", longBio: "", image: "", tier: "leadership", published: true }] })}><Plus size={13} /> Add person</button>
                  </div>
                  <div className="mt-3 grid gap-4">
                    <div><label className={LBL}>Section eyebrow</label><input className={INPUT} data-testid="af-leadersEyebrow" value={about.leadersEyebrow || ""} onChange={(e) => setAbout({ ...about, leadersEyebrow: e.target.value })} /></div>
                    <div><label className={LBL}>Section heading</label><input className={INPUT} data-testid="af-leadersHeading" value={about.leadersHeading || ""} onChange={(e) => setAbout({ ...about, leadersHeading: e.target.value })} /></div>
                  </div>
                  <div className="mt-4 space-y-4">
                    {(about.leaders || []).map((l, i) => {
                      const upd = (patch) => setAbout({ ...about, leaders: about.leaders.map((x, j) => (j === i ? { ...x, ...patch } : x)) });
                      return (
                        <div key={i} className="border border-white/10 bg-white/[0.03] p-3" data-testid={`af-leader-${i}`}>
                          <div className="mb-2 flex items-center justify-between">
                            <span className="font-body text-[10px] font-semibold uppercase tracking-[0.14em] text-[#5f8f10]">Person {i + 1}</span>
                            <button type="button" aria-label="Remove person" className="text-white/40 hover:text-red-400" onClick={() => setAbout({ ...about, leaders: about.leaders.filter((_, j) => j !== i) })}><Trash2 size={13} /></button>
                          </div>
                          <div className="grid gap-3 md:grid-cols-2">
                            <input className={INPUT} value={l.name} placeholder="Name" onChange={(e) => upd({ name: e.target.value })} />
                            <input className={INPUT} value={l.role} placeholder="Role / title" onChange={(e) => upd({ role: e.target.value })} />
                          </div>
                          <div className="mt-3 grid items-center gap-3 md:grid-cols-2">
                            <select className={INPUT} data-testid={`af-leader-tier-${i}`} value={l.tier || "leadership"} onChange={(e) => upd({ tier: e.target.value })}>
                              <option value="leadership">Leadership (shown on About + Team)</option>
                              <option value="team">Team (shown on Team page only)</option>
                            </select>
                            <label className="font-body flex items-center gap-2 text-sm text-white/70">
                              <input type="checkbox" data-testid={`af-leader-pub-${i}`} checked={l.published !== false} onChange={(e) => upd({ published: e.target.checked })} /> Published
                            </label>
                          </div>
                          <textarea rows={2} className={`${INPUT} mt-3`} value={l.bio} placeholder="Short bio (shown on cards)" onChange={(e) => upd({ bio: e.target.value })} />
                          <textarea rows={3} className={`${INPUT} mt-3`} data-testid={`af-leader-longbio-${i}`} value={l.longBio || ""} placeholder="Longer profile / bio (shown on the Team page)" onChange={(e) => upd({ longBio: e.target.value })} />
                          <input className={`${INPUT} mt-3`} value={l.image} placeholder="Photo URL (optional — paste /assets/... or https://...)" onChange={(e) => upd({ image: e.target.value })} />
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <button onClick={saveAbout} disabled={saving} data-testid="admin-about-save" className={`${BTN} disabled:opacity-60`}><Save size={15} /> {saving ? "Saving…" : "Save About content"}</button>
                  {err && <span className="text-sm font-semibold text-red-400">{err}</span>}
                </div>
              </div>
            )}
          </div>
        )}

        {tab === "home" && (
          <div className="mt-6" data-testid="admin-home-editor">
            {home === null ? (
              <p className="font-body text-white/50">Loading…</p>
            ) : (
              <>
                <p className="font-body mb-6 max-w-2xl text-sm text-white/50">Tip: press <span className="text-white/80">Enter</span> for a line break, and wrap a word in <span className="text-[#5f8f10]">**double asterisks**</span> to highlight it in green.</p>
                <div className="space-y-8">
                  {HOME_GROUPS.map((g) => (
                    <div key={g.title} className="border border-white/12 p-4 md:p-5">
                      <h3 className="font-display mb-4 text-lg uppercase tracking-tight text-[#5f8f10]">{g.title}</h3>
                      <div className="space-y-4">
                        {g.fields.map(([k, label, area]) => (
                          <div key={k}>
                            <label className={LBL}>{label}</label>
                            {area
                              ? <textarea rows={2} className={INPUT} data-testid={`hf-${k}`} value={home[k] || ""} onChange={(e) => setHome({ ...home, [k]: e.target.value })} />
                              : <input className={INPUT} data-testid={`hf-${k}`} value={home[k] || ""} onChange={(e) => setHome({ ...home, [k]: e.target.value })} />}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-6 flex items-center gap-3">
                  <button onClick={saveHome} disabled={saving} data-testid="admin-home-save" className={`${BTN} disabled:opacity-60`}><Save size={15} /> {saving ? "Saving…" : "Save homepage copy"}</button>
                  {err && <span className="text-sm font-semibold text-red-400">{err}</span>}
                </div>
              </>
            )}
          </div>
        )}

        {tab === "blogs" && (
          <div className="mt-6 border-t border-white/12" data-testid="admin-blog-list">
            {blogs.map((b) => (
              <div key={b.slug} className="flex items-center justify-between gap-4 border-b border-white/12 py-4">
                <div className="flex items-center gap-4">
                  <span className="font-display text-xl text-[#5f8f10]">{b.number}</span>
                  <div>
                    <p className="font-body inline-flex items-center gap-2 text-base">{b.title} {b.featured && <span className="inline-flex items-center gap-1 border border-[#5f8f10] px-1.5 py-0.5 text-[10px] uppercase tracking-[0.1em] text-[#5f8f10]"><Star size={10} /> Featured</span>}</p>
                    <p className="font-body text-xs uppercase tracking-[0.1em] text-white/40">{b.situation} {b.published ? "" : "· DRAFT"}{b.published && b.publishAt && new Date(b.publishAt) > new Date() ? ` · SCHEDULED ${new Date(b.publishAt).toLocaleDateString()}` : ""}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => copyPreview("blog", b.slug)} data-testid={`admin-preview-${b.slug}`} title="Copy preview link" className={SMALL}><Eye size={14} /> Preview</button>
                  <button onClick={() => editBlog(b)} data-testid={`admin-edit-${b.slug}`} className={GHOST}>Edit</button>
                  <button onClick={() => remove("blog", b.slug)} data-testid={`admin-delete-${b.slug}`} aria-label="Delete" className={ICON}><Trash2 size={15} /></button>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "cases" && (
          <div className="mt-6 border-t border-white/12" data-testid="admin-case-list">
            <p className="font-body py-3 text-xs uppercase tracking-[0.1em] text-white/40">Drag the handle (or use the arrows) to reorder how cases appear on Our Work.</p>
            {cases.map((c, ci) => (
              <div
                key={c.slug}
                draggable
                onDragStart={() => setDragSlug(c.slug)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => onDropCase(c.slug)}
                className={`flex items-center justify-between gap-4 border-b border-white/12 py-4 ${dragSlug === c.slug ? "opacity-50" : ""}`}
                data-testid={`admin-case-row-${c.slug}`}
              >
                <div className="flex min-w-0 items-center gap-3">
                  <span className="cursor-grab text-white/40 active:cursor-grabbing" aria-label="Drag to reorder"><GripVertical size={16} /></span>
                  <div className="flex flex-col gap-0.5">
                    <button type="button" aria-label="Move up" onClick={() => moveCase(c.slug, -1)} className="text-white/40 transition-colors hover:text-[#5f8f10]"><ArrowUp size={13} /></button>
                    <button type="button" aria-label="Move down" onClick={() => moveCase(c.slug, 1)} className="text-white/40 transition-colors hover:text-[#5f8f10]"><ArrowDown size={13} /></button>
                  </div>
                  <div className="min-w-0">
                    <p className="font-body truncate text-base">{c.headline}</p>
                    <p className="font-body text-xs uppercase tracking-[0.1em] text-white/40">{c.kicker} · {c.situation} {c.published ? "" : "· DRAFT"}</p>
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <button onClick={() => copyPreview("case", c.slug)} data-testid={`admin-preview-case-${c.slug}`} title="Copy preview link" className={SMALL}><Eye size={14} /> Preview</button>
                  <button onClick={() => editCase(c)} data-testid={`admin-edit-case-${c.slug}`} className={GHOST}>Edit</button>
                  <button onClick={() => remove("case", c.slug)} data-testid={`admin-delete-case-${c.slug}`} aria-label="Delete" className={ICON}><Trash2 size={15} /></button>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "growth" && (
          <div className="mt-6 border-t border-white/12" data-testid="admin-growth-list">
            <p className="font-body py-3 text-xs uppercase tracking-[0.1em] text-white/40">The four growth-situation pages under /what-we-solve.</p>
            {situations.map((s) => (
              <div key={s.slug} className="flex items-center justify-between gap-4 border-b border-white/12 py-4">
                <div className="flex items-center gap-4">
                  <span className="font-display text-xl text-[#5f8f10]">{s.number}</span>
                  <p className="font-body text-base">{s.title}</p>
                </div>
                <button onClick={() => editSituation(s)} data-testid={`admin-edit-situation-${s.slug}`} className={GHOST}>Edit</button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
